import { Reducer, useMemo, useReducer } from "react";
import { IfAny, Values } from "types/utilities";

export type BaseServiceType = {
    [key: string]: (...args: any[]) => any;
};

export type ServiceStore<ServiceType extends BaseServiceType> = {
    [key in keyof ServiceType]: IfAny<
        Awaited<ReturnType<ServiceType[key]>>,
        never,
        Awaited<ReturnType<ServiceType[key]>>
    >;
};

type ServiceStoreAction<ServiceType extends BaseServiceType> = Values<{
    [key in keyof ServiceType]: {
        name: key;
        state: Awaited<ReturnType<ServiceType[key]>>;
    };
}>;

export type ServiceStoreFns<ServiceType extends BaseServiceType> = {
    [key in keyof ServiceType]: ServiceType[key];
};

export const useServiceWithStore = <ServiceType extends BaseServiceType>(service: ServiceType) => {
    const [store, dispatch] = useReducer<
        Reducer<ServiceStore<ServiceType>, ServiceStoreAction<ServiceType>>
    >((prev, action) => {
        return {
            ...prev,
            [action.name]: action.state,
        };
    }, {} as ServiceStore<ServiceType>);

    // @ts-ignore
    const serviceFnsWithDispatch: ServiceStoreFns<ServiceType> = useMemo(() => {
        if (!service) return null;
        const generated = {};
        for (const s in service) {
            // @ts-ignore
            generated[s] = (...args) => {
                return service[s](...args).then((res: unknown) => {
                    // @ts-ignore generic service return type cannot be recovered from a dynamic key
                    dispatch({ name: s, state: res });
                    return res;
                });
            };
        }
        return generated;
    }, [service]);

    return { store, serviceFnsWithDispatch };
};
